"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EvidenceAddress = void 0;
const evidence_errors_1 = require("./evidence.errors");
const evidence_vote_types_1 = require("./evidence-vote.types");
class EvidenceAddress {
    static { this.SEPARATOR = '/'; }
    static { this.FILE_SEGMENT = 'file'; }
    static { this.PAGE_SEGMENT = 'page'; }
    static { this.ELEMENT_SEGMENT = 'element'; }
    static { this.ROW_SEGMENT = 'row'; }
    static { this.CHUNK_SEGMENT = 'chunk'; }
    static { this.POSITIVE_INTEGER = /^[1-9]\d*$/; }
    static { this.NON_NEGATIVE_INTEGER = /^(0|[1-9]\d*)$/; }
    static { this.GRAINS = [
        {
            kind: evidence_vote_types_1.EvidenceLocatorKind.Page,
            keyword: EvidenceAddress.PAGE_SEGMENT,
            under: [evidence_vote_types_1.EvidenceLocatorKind.File],
            values: ['n'],
            reserved: false,
        },
        {
            kind: evidence_vote_types_1.EvidenceLocatorKind.Element,
            keyword: EvidenceAddress.ELEMENT_SEGMENT,
            under: [evidence_vote_types_1.EvidenceLocatorKind.Page],
            values: ['provider', 'index'],
            reserved: false,
        },
        {
            kind: evidence_vote_types_1.EvidenceLocatorKind.Row,
            keyword: EvidenceAddress.ROW_SEGMENT,
            under: [evidence_vote_types_1.EvidenceLocatorKind.Element],
            values: ['provider', 'index'],
            reserved: false,
        },
        {
            kind: evidence_vote_types_1.EvidenceLocatorKind.Chunk,
            keyword: EvidenceAddress.CHUNK_SEGMENT,
            under: [evidence_vote_types_1.EvidenceLocatorKind.Page, evidence_vote_types_1.EvidenceLocatorKind.Element],
            values: ['provider', 'index'],
            reserved: true,
        },
    ]; }
    static file(fileId) {
        const safeFileId = EvidenceAddress.token(fileId, 'file id', fileId);
        return [EvidenceAddress.FILE_SEGMENT, safeFileId].join(EvidenceAddress.SEPARATOR);
    }
    static page(fileId, pageNumber) {
        const fileAddress = EvidenceAddress.file(fileId);
        EvidenceAddress.assertInteger(pageNumber, 1, 'page number', fileAddress);
        return [fileAddress, EvidenceAddress.PAGE_SEGMENT, String(pageNumber)].join(EvidenceAddress.SEPARATOR);
    }
    static element(fileId, pageNumber, provider, elementIndex) {
        const pageAddress = EvidenceAddress.page(fileId, pageNumber);
        const safeProvider = EvidenceAddress.token(provider, 'provider', pageAddress);
        EvidenceAddress.assertInteger(elementIndex, 0, 'element index', pageAddress);
        return [pageAddress, EvidenceAddress.ELEMENT_SEGMENT, safeProvider, String(elementIndex)].join(EvidenceAddress.SEPARATOR);
    }
    static row(elementAddress, rowProvider, rowIndex) {
        if (EvidenceAddress.parse(elementAddress).kind !== evidence_vote_types_1.EvidenceLocatorKind.Element) {
            throw new evidence_errors_1.InvalidEvidenceAddressError(elementAddress, 'a row must sit under an element');
        }
        const safeProvider = EvidenceAddress.token(rowProvider, 'row provider', elementAddress);
        EvidenceAddress.assertInteger(rowIndex, 0, 'row index', elementAddress);
        return [elementAddress, EvidenceAddress.ROW_SEGMENT, safeProvider, String(rowIndex)].join(EvidenceAddress.SEPARATOR);
    }
    static parentOf(address) {
        const parsed = EvidenceAddress.parse(address);
        switch (parsed.kind) {
            case evidence_vote_types_1.EvidenceLocatorKind.Row:
                return EvidenceAddress.element(parsed.fileId, parsed.page, parsed.provider, parsed.elementIndex);
            case evidence_vote_types_1.EvidenceLocatorKind.Element:
                return EvidenceAddress.page(parsed.fileId, parsed.page);
            case evidence_vote_types_1.EvidenceLocatorKind.Page:
                return EvidenceAddress.file(parsed.fileId);
            case evidence_vote_types_1.EvidenceLocatorKind.File:
                return null;
        }
    }
    static likePrefixPattern(prefix) {
        if (!prefix.endsWith(EvidenceAddress.SEPARATOR)) {
            throw new evidence_errors_1.InvalidEvidenceAddressError(prefix, `a prefix must end with '${EvidenceAddress.SEPARATOR}'`);
        }
        return `${prefix.replace(/[\\%_]/g, '\\$&')}%`;
    }
    static parse(address) {
        const [fileKeyword, fileId, ...rest] = address.split(EvidenceAddress.SEPARATOR);
        if (fileKeyword !== EvidenceAddress.FILE_SEGMENT || fileId === undefined || fileId === '') {
            throw new evidence_errors_1.InvalidEvidenceAddressError(address, `expected '${EvidenceAddress.FILE_SEGMENT}/{fileId}'`);
        }
        let parsed = { kind: evidence_vote_types_1.EvidenceLocatorKind.File, fileId };
        let remaining = rest;
        while (remaining.length > 0) {
            const [keyword, ...afterKeyword] = remaining;
            const parentKind = parsed.kind;
            const grain = EvidenceAddress.GRAINS.find((candidate) => candidate.keyword === keyword && candidate.under.includes(parentKind));
            if (grain === undefined) {
                throw new evidence_errors_1.InvalidEvidenceAddressError(address, `'${keyword}' cannot follow '${parentKind}'`);
            }
            if (grain.reserved) {
                throw new evidence_errors_1.InvalidEvidenceAddressError(address, `'${grain.keyword}' regions are not yet addressable`);
            }
            const values = afterKeyword.slice(0, grain.values.length);
            if (values.length < grain.values.length) {
                const usage = [grain.keyword, ...grain.values.map((name) => `{${name}}`)].join(EvidenceAddress.SEPARATOR);
                throw new evidence_errors_1.InvalidEvidenceAddressError(address, `expected '${usage}' under '${parentKind}'`);
            }
            parsed = EvidenceAddress.descend(parsed, grain, values, address);
            remaining = afterKeyword.slice(grain.values.length);
        }
        return parsed;
    }
    static descend(parent, grain, values, address) {
        const [first = '', second = ''] = values;
        if (grain.kind === evidence_vote_types_1.EvidenceLocatorKind.Page && parent.kind === evidence_vote_types_1.EvidenceLocatorKind.File) {
            const page = EvidenceAddress.parseInteger(first, EvidenceAddress.POSITIVE_INTEGER, 'page number', address);
            return { ...parent, kind: evidence_vote_types_1.EvidenceLocatorKind.Page, page };
        }
        if (grain.kind === evidence_vote_types_1.EvidenceLocatorKind.Element && parent.kind === evidence_vote_types_1.EvidenceLocatorKind.Page) {
            return {
                ...parent,
                kind: evidence_vote_types_1.EvidenceLocatorKind.Element,
                provider: EvidenceAddress.token(first, 'provider', address),
                elementIndex: EvidenceAddress.parseInteger(second, EvidenceAddress.NON_NEGATIVE_INTEGER, 'element index', address),
            };
        }
        if (grain.kind === evidence_vote_types_1.EvidenceLocatorKind.Row && parent.kind === evidence_vote_types_1.EvidenceLocatorKind.Element) {
            return {
                ...parent,
                kind: evidence_vote_types_1.EvidenceLocatorKind.Row,
                rowProvider: EvidenceAddress.token(first, 'row provider', address),
                rowIndex: EvidenceAddress.parseInteger(second, EvidenceAddress.NON_NEGATIVE_INTEGER, 'row index', address),
            };
        }
        throw new evidence_errors_1.InvalidEvidenceAddressError(address, `'${grain.keyword}' cannot follow '${parent.kind}'`);
    }
    static token(value, label, context) {
        if (value === '' || value.includes(EvidenceAddress.SEPARATOR)) {
            throw new evidence_errors_1.InvalidEvidenceAddressError(context, `${label} '${value}' must be non-empty and free of '${EvidenceAddress.SEPARATOR}'`);
        }
        return value;
    }
    static assertInteger(value, minimum, label, context) {
        if (!Number.isSafeInteger(value) || value < minimum) {
            throw new evidence_errors_1.InvalidEvidenceAddressError(context, `${label} ${value} must be an integer >= ${minimum}`);
        }
    }
    static parseInteger(token, shape, label, address) {
        const value = Number(token);
        if (!shape.test(token) || !Number.isSafeInteger(value)) {
            throw new evidence_errors_1.InvalidEvidenceAddressError(address, `${label} '${token}' is not a canonical integer`);
        }
        return value;
    }
}
exports.EvidenceAddress = EvidenceAddress;
//# sourceMappingURL=evidence-address.js.map